// What a bulk cross-listing run looks like on screen: the server's per-listing batch results
// with the Chrome extension's own outcomes overlaid, the totals across the whole run, and the
// single status every view of that run reads.
//
// Server rows key each platform outcome either by the bare platform name or by
// `platform:integrationId` (see platform-keys.js). Outcomes coming back from the extension are
// keyed by the bare platform name, which is exactly the key the server used for the
// `pendingExtension` entry each one resolves.

import { mapPlatformError } from 'src/utils/platform-error-message';
import { isUnconfirmedCreate, unconfirmedCreateFlags } from 'src/utils/extension-publish-rules';

/**
 * The rendered form of one extension outcome.
 * @param {{success:boolean, error?:string, alreadyCreated?:boolean, soldOut?:boolean,
 *   timedOut?:boolean, unrecorded?:boolean, marketplaceUnconfirmed?:boolean}} outcome
 */
function extensionOverlay(outcome) {
  if (outcome.success) {
    const alreadyCreated = outcome.alreadyCreated || false;
    const created = { success: true, extensionCreated: !alreadyCreated, alreadyCreated };
    // Nothing was posted because the item sold out — said as such, never as already listed.
    return outcome.soldOut ? { ...created, soldOut: true } : created;
  }

  // A create whose post may be live on the marketplace is carried through as its own thing, with
  // the reason it is unconfirmed. It is not a failure and must not be offered as one: the row is
  // shown, counted and retried differently, and every one of those decisions is made by asking
  // isUnconfirmedCreate about this same row.
  if (isUnconfirmedCreate(outcome)) {
    return { success: false, error: outcome.error, ...unconfirmedCreateFlags(outcome) };
  }

  // The marketplace answered and refused the post, so nothing was created and re-running the
  // create is safe. API failures keep whatever the server decided about them.
  return { success: false, error: outcome.error, retryable: true };
}

/**
 * Overlays the extension's outcomes onto the server's batch results and recounts each listing.
 *
 * @param {Array<object>} serverResults - the batch status `results` array from the server
 * @param {Object<string, Object<string, object>>} extensionResults - outcomes as
 *   `{ [listingId]: { [platform]: { success, error?, alreadyCreated? } } }`
 * @returns {Array<object>} the same rows with merged `results` and fresh
 *   `successCount` / `failureCount` / `unconfirmedCount` / `pendingCount`
 */
export function mergeExtensionResults(serverResults, extensionResults) {
  return (serverResults || []).map((row) => {
    const outcomes = (extensionResults || {})[row.listingId] || {};

    const results = { ...row.results };
    Object.entries(outcomes).forEach(([platform, outcome]) => {
      results[platform] = extensionOverlay(outcome);
    });

    // Counts are recomputed rather than trusted from the server, because an entry the extension
    // still owes us is neither a success nor a failure until it resolves.
    //
    // A listing whose whole job failed is the one row with nothing to recount: the server sends
    // it with no per-marketplace results at all and counts its failures for it, so its own counts
    // are kept — recounting an empty row would report a failed listing as a clean one.
    const entries = Object.values(results);
    if (entries.length === 0) {
      return {
        ...row,
        results,
        successCount: row.successCount || 0,
        failureCount: row.failureCount || 0,
        unconfirmedCount: 0,
        pendingCount: row.pendingCount || 0,
      };
    }

    // A marketplace whose post may be live is counted on its own, and kept out of the failures.
    // Counting it as failed is what told a seller their item did not go up when it had, and it is
    // what put the item in front of a Retry button that would have posted it a second time.
    return {
      ...row,
      results,
      successCount: entries.filter((entry) => entry.success && !entry.pendingExtension).length,
      failureCount: entries.filter(
        (entry) => !entry.success && !entry.pendingExtension && !isUnconfirmedCreate(entry)
      ).length,
      unconfirmedCount: entries.filter(isUnconfirmedCreate).length,
      pendingCount: entries.filter((entry) => entry.pendingExtension).length,
    };
  });
}

/**
 * Totals across every listing of a run.
 * @param {Array<object>} mergedResults - output of mergeExtensionResults
 * @returns {{totalSuccess:number, totalFailed:number, totalUnconfirmed:number, totalPending:number}}
 */
export function countRunResults(mergedResults) {
  return (mergedResults || []).reduce(
    (totals, row) => ({
      totalSuccess: totals.totalSuccess + (row.successCount || 0),
      totalFailed: totals.totalFailed + (row.failureCount || 0),
      totalUnconfirmed: totals.totalUnconfirmed + (row.unconfirmedCount || 0),
      totalPending: totals.totalPending + (row.pendingCount || 0),
    }),
    { totalSuccess: 0, totalFailed: 0, totalUnconfirmed: 0, totalPending: 0 }
  );
}

/**
 * Did this run go through with nothing at all left for the seller?
 *
 * What the congratulations at the end of a run is shown on, so it is deliberately strict: a run is
 * clean only when nothing failed, nothing was left uncreated, AND nothing ended without our being
 * able to say where it stands. Congratulating someone whose items may or may not have gone up is
 * how they stop believing the run report at all.
 *
 * @param {Array<object>} mergedResults - output of mergeExtensionResults
 * @returns {boolean}
 */
export function isCleanRun(mergedResults) {
  return (mergedResults || []).every((row) => (
    (row.failureCount || 0) === 0
    && (row.unconfirmedCount || 0) === 0
    && (row.pendingCount || 0) === 0
  ));
}

/**
 * The one status a run is in, from the server's batch status and the extension's own phase.
 *
 * @param {Object} input
 * @param {string} [input.serverStatus] - batch status string: 'queued' | 'processing' |
 *   'completed' | 'not_found', or absent before the first poll answers
 * @param {string} [input.extensionPhase] - 'idle' | 'processing' | 'paused' | 'done'
 * @param {{totalFailed?:number, totalUnconfirmed?:number, totalPending?:number}} [input.counts] -
 *   from countRunResults
 * @param {number} [input.failedListings] - the server's own count of listings whose job failed
 * @param {boolean} [input.isStopped] - the seller stopped this run; nothing left will be created
 * @param {boolean} [input.isFollowed] - this browser is following this run, so work still owed to
 *   it is on its way. False for a run nobody is following — a page showing an earlier run, or one
 *   the server has forgotten — where nothing left over is ever going to be created
 * @param {boolean} [input.extensionUnavailable] - the extension did not answer, so the rest of
 *   this run is not being created and will not be until the seller does something
 * @returns {'queued'|'processing'|'creating_via_extension'|'needs_extension'|'paused'|'stopped'|'completed'|'completed_with_errors'|'not_found'}
 */
export function deriveRunStatus({
  serverStatus,
  extensionPhase,
  counts,
  failedListings = 0,
  isStopped = false,
  isFollowed = true,
  extensionUnavailable = false,
} = {}) {
  const { totalFailed = 0, totalUnconfirmed = 0, totalPending = 0 } = counts || {};

  if (serverStatus === 'not_found') return 'not_found';

  // The extension's own phase describes the run better than the batch status behind it — the
  // batch has already finished by the time the extension starts creating anything, and a run
  // waiting for the user to resume it is neither queued nor finished.
  //
  // A run the seller stopped is the exception: it is over from the moment they said so, even
  // while the create already in flight finishes, and nothing about it may read as resumable.
  if (!isStopped) {
    if (extensionPhase === 'processing') return 'creating_via_extension';
    if (extensionPhase === 'paused') return 'paused';
  }

  // Answered before anything below reads work as on its way. With no extension answering, the
  // posts this run still owes are not being made and nothing is going to change that on its own.
  if (extensionUnavailable && !isStopped && totalPending > 0) return 'needs_extension';

  if (serverStatus === 'completed') {
    // Entries still marked pendingExtension mean extension work is queued or in flight. This
    // also covers the short gap between the batch finishing and the first create starting —
    // unless the seller stopped the run, in which case those entries are what they chose to skip,
    // or nobody is following the run any more, in which case they are what it left behind: the
    // run is over either way, and reading it as still creating would be a wait that never ends.
    if (totalPending > 0) {
      if (isStopped) return 'stopped';
      if (isFollowed) return 'creating_via_extension';
      return 'completed_with_errors';
    }

    if (isStopped || extensionPhase === 'done' || extensionPhase === 'idle') {
      // A listing whose job failed outright carries no per-platform results at all, so its own
      // counts — and the server's count of failed listings — are what reveal it.
      //
      // A marketplace left unconfirmed keeps the run out of 'completed' just as a failure does.
      // Nothing failed there, but the seller still has to go and look, and a run that reads as
      // cleanly done is a run nobody opens again.
      const nothingLeftToLookAt = failedListings === 0 && totalFailed === 0 && totalUnconfirmed === 0;
      return nothingLeftToLookAt ? 'completed' : 'completed_with_errors';
    }
  }

  if (serverStatus === 'processing') return 'processing';
  return 'queued';
}

/**
 * What a page has to work from for a run this browser is not following.
 *
 * Neither half of it is the whole picture on its own: the server never hears about the creates this
 * browser made for it, and the copy this browser saved can be older than the server's own record.
 * So the server's record is preferred when it still has one, the saved copy stands in when it does
 * not, and the creates this browser made are carried either way.
 *
 * The source is never 'live': only the one run this browser is following is live, because the
 * extension works on that run alone (see describePlatformRow for what each source means on screen).
 *
 * @param {Object|null} serverStatus - the run as the server still remembers it, or null
 * @param {Object|null} snapshot - what this browser saved of the run (see utils/batch-snapshot.js)
 * @returns {{serverStatus: Object|null, extensionResults: Object, source: string, stoppedAt: number|null}}
 */
export function composeInspectedRun(serverStatus, snapshot) {
  // Whether the seller stopped this run, and what this browser created for it, are things only this
  // browser knows — they are read from the saved copy whichever way the results themselves arrive.
  const stoppedAt = snapshot?.stoppedAt || null;
  const extensionResults = snapshot?.extensionResults || {};

  if (serverStatus) return { serverStatus, extensionResults, source: 'server', stoppedAt };

  if (snapshot?.serverStatus) {
    return { serverStatus: snapshot.serverStatus, extensionResults, source: 'snapshot', stoppedAt };
  }

  return { serverStatus: null, extensionResults: {}, source: 'expired', stoppedAt };
}

/**
 * Every create the browser tried and failed that is worth another attempt — what a
 * "retry everything that failed" action works through, and the same set the button offering it
 * is counted from.
 *
 * A create whose post may be live is never in this list. Retrying one is how the same item gets
 * posted twice: the server's claim stops a second post only while it is fresh, and by the time a
 * finished run is on screen it rarely is. Those rows are shown with what to check instead.
 *
 * @param {Object<string, Object<string, object>>} extensionResults - `{ listingId: { platform: result } }`
 * @returns {Array<{listingId:string, platform:string}>}
 */
export function listRetryableFailures(extensionResults) {
  return Object.entries(extensionResults || {}).flatMap(([listingId, byPlatform]) =>
    Object.entries(byPlatform || {})
      .filter(
        ([platform, result]) =>
          !result?.success &&
          !isUnconfirmedCreate(result) &&
          mapPlatformError(result?.error, platform).retryable !== false
      )
      .map(([platform]) => ({ listingId, platform }))
  );
}

/**
 * Whether a run updated a post an account already held (`editQueued`) — a Whatnot listing hidden there,
 * say. That update waits in the extension's own queue, which runs only when something wakes it.
 *
 * @param {Object|null} serverStatus - the batch status the server serves
 * @returns {boolean}
 */
export function leftUpdatesQueued(serverStatus) {
  return (serverStatus?.results || []).some((result) =>
    Object.values(result.results || {}).some((platformResult) => platformResult?.editQueued === true));
}
